// PRODUCT-26110 Phase 2B2: 'vue-router/types/router' is a vue-router-3-only subpath,
// not present in v4's `exports` map at all -- module-not-found, not just a type
// mismatch. `Route` -> `RouteLocationNormalized`.
import type { RouteLocationNormalized } from 'vue-router';

import { watch } from 'vue';

import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
import { RelyingParty } from '@evidentid/dashboard-commons/modules/auth/types';
import {
    getAvailableFeaturesMapByRole,
} from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/get-available-features-map-by-role/getAvailableFeaturesMapByRole';
import { useRbacStore, UserRolesStatus } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
import { RbacRole } from '@evidentid/tprm-portal-lib/models/rbac/RbacRole.model';
import createMetaNavigationGuard from '@evidentid/vue-commons/router/createMetaNavigationGuard';

import { useContractExtractionStore } from '@/modules/contract-extraction/store';
import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

/* eslint-disable no-use-before-define */
export {
    mountAppToRootElement,
    partnerCustomizationSetup,
    watchRpChangeAndLoadCategorizedEnumLabels,
    watchRpChangeAndResumeContractExtractionPolling,
    watchRpChangeLoadUserRolesAndTryParseRbacFeatures,
    watchUserRolesChangesAndTryParseRbacFeatures,
    watchUserTokenRpRoleMapChangesAndTryParseRbacFeatures,
};

function mountAppToRootElement(app: any) {
    // Find 'root' element
    const rootElement = document.querySelector('#root');

    // Mount application
    if (rootElement) {
        app.mount(rootElement);
    } else {
        throw new Error('Unable to find #root element.');
    }
}

function partnerCustomizationSetup(app: any) {
    /**
     * code bellow is a copy of one inside `PartnersSettingsService`
     * that's because we need to make sure that we always display proper branding
     * Thanks to loading animation bellow we need to do it in two places. Separately.
     */

    const environmentToUrlMap = {
        dev: 'https://privacy-staging.evidentid.com/partner-profiles/config-dev/',
        int: 'https://privacy-staging.evidentid.com/partner-profiles/config-int/',
        demo: 'https://privacy.evidentid.com/partner-profiles/config-demo/',
        production: 'https://privacy.evidentid.com/partner-profiles/config/',
    };

    function extractPartnerName() {
        const URL_PARTS_LENGTH_WITH_PARTNER_NAME = 4;
        const currentLocation = window.location.href;
        const locationWithoutHttps = currentLocation.split('//')[1];
        const urlParts = locationWithoutHttps.split('.');
        if (urlParts.length < URL_PARTS_LENGTH_WITH_PARTNER_NAME) {
            return undefined;
        }
        return urlParts[0];
    }

    function getPartnerSettingsUrl(partnerName: string, environment: string) {
        // @ts-ignore
        const baseUrl = environmentToUrlMap[environment];
        return `${baseUrl + partnerName}.json`;
    }

    const environment = import.meta.env.WEB_PUBLIC_ENVIRONMENT;
    const partnerName = extractPartnerName();
    const favIconElement = document.getElementById('favicon');
    if (partnerName) {
        const url = getPartnerSettingsUrl(partnerName, environment);
        try {
            fetch(url, { cache: 'no-cache' })
                .then((response) => response.json())
                .then((partnerSettings) => {
                    if (partnerSettings && partnerSettings.name) {
                        // Set-up meta <title> automatically per route
                        // TODO: Use some Vue component to do that instead
                        app.router.beforeEach(createMetaNavigationGuard(partnerSettings.name, document));
                        favIconElement?.setAttribute('href', partnerSettings.favicon_url);
                    }
                });
        } catch (e) {
            console.error(e);
            // Set-up meta <title> automatically per route
            // TODO: Use some Vue component to do that instead
            app.router.beforeEach(createMetaNavigationGuard('Evident', document));
        }
    } else {
        // Set-up meta <title> automatically per route
        // TODO: Use some Vue component to do that instead
        app.router.beforeEach(createMetaNavigationGuard('Evident', document));
    }
}

function watchRpChangeAndLoadCategorizedEnumLabels(app: any) {
    watch(app.router.currentRoute, (newRoute: any, oldRoute: any) => {
        if (newRoute.params.rpId && newRoute.params.rpId !== oldRoute.params.rpId) {
            useDashboardStore().loadCategorizedEnumLabels({ rpName: newRoute.params.rpId });
        }
    });
}

function watchRpChangeAndResumeContractExtractionPolling(app: any) {
    watch(app.router.currentRoute, (newRoute: any, oldRoute: any) => {
        if (newRoute.params.rpId && newRoute.params.rpId !== oldRoute.params.rpId) {
            if (oldRoute.params.rpId) {
                useContractExtractionStore().stopPolling({
                    rpName: oldRoute.params.rpId,
                });
            }
            useContractExtractionStore().initializePolling({
                rpName: newRoute.params.rpId,
            });
        }
    });
}

function _getAndUpdateUserFeatureFlags(app: any, rpName: string): void {
    const role = useUserStore(app.getPinia()).relyingParties?.find((rp) => rp.name === rpName)?.role;
    if (!role) {
        throw new Error(`Role not found for RP ${rpName}`);
    }
    const features = getAvailableFeaturesMapByRole(role, useRbacStore(app.getPinia()).userRoles.list || []);
    const userStore = useUserStore(app.getPinia());
    userStore.updateCurrentRpRole(role);
    userStore.updateCurrentRpRbacFeatures(features);
}

function watchRpChangeLoadUserRolesAndTryParseRbacFeatures(app: any) {
    watch(
        app.router.currentRoute,
        async (newRoute: RouteLocationNormalized, oldRoute: RouteLocationNormalized) => {
            if (newRoute.params.rpId && newRoute.params.rpId !== oldRoute.params.rpId) {
                const rbacStore = useRbacStore(app.getPinia());
                await rbacStore.loadUserRoles(newRoute.params.rpId as string);
                if (rbacStore.userRoles.status === 'success' &&
                    useUserStore(app.getPinia()).relyingParties != null
                ) {
                    _getAndUpdateUserFeatureFlags(app, newRoute.params.rpId as string);
                }
            }
        });
}

function watchUserRolesChangesAndTryParseRbacFeatures(app: any) {
    watch(
        () => useRbacStore(app.getPinia()).userRoles,
        (userRoles: UserRolesStatus) => {
            if (userRoles.status === 'success' && useUserStore(app.getPinia()).relyingParties != null
            ) {
                _getAndUpdateUserFeatureFlags(app, app.router.currentRoute.value.params.rpId);
            }
        });
}

function watchUserTokenRpRoleMapChangesAndTryParseRbacFeatures(app: any) {
    watch(
        () => useUserStore(app.getPinia()).relyingParties,
        (rpRoleMap: RelyingParty[] | null) => {
            if (rpRoleMap != null && useRbacStore(app.getPinia()).userRoles.status === 'success') {
                _getAndUpdateUserFeatureFlags(app, app.router.currentRoute.value.params.rpId);
            }
        });
}
